import type {
  CoverData as CoverDataWire,
  CreateCartItemData,
  CustomWorkbookCartItemCreationRequest,
  CustomWorkbookCartItemUpdateRequest,
  DownloadCartItemPdfFileData,
  GetCartItemData,
  GetCartItemParams,
  PrintDocumentCartItemCreationRequest,
  PrintDocumentCartItemUpdateRequest,
  SignatureWorkbookCartItemCreationRequest,
  SignatureWorkbookCartItemUpdateRequest,
  UpdateCartItemData,
} from '~/api-spec/__generated__/pod'
import { httpClient } from '~/http/client'
import type { WithNullableOptionals } from '~/http/request'

import { adaptCart, adaptCartItemCreation, adaptCartItemPrice, adaptPdfDownload } from './adapters'
import type { Cart, CartItem, CartItemPrice, PdfDownload } from './schemas'

// 서버는 요청의 선택 필드(parentOrders·workbookParts·additionalPrice, 표지 데이터의 subtitle 등)를
// List?·Int?·ObjectNode? 로 받지만 스펙이 nullable 을 빠뜨려 optional 로만 그린다. 선택 필드에 null 을 되살린다.
// 2026-10-06 POD 서버 develop(edfdcec) 의 cartItem/controller/request/CartItemCreationRequest.kt·
// CartItemUpdateRequest.kt·product/model/type/CoverData.kt 로 확인.
export type CoverDataPayload = WithNullableOptionals<CoverDataWire>

// 서버는 productType 으로 요청 클래스를 고르는데(@JsonTypeInfo) 스펙은 종류마다 같은 4값 유니온을 그린다.
// 요청 타입마다 자기 종류로 좁힌다.
type CartItemRequest<Request, ProductType extends CartItem['productType']> = WithNullableOptionals<
  Omit<Request, 'coverData' | 'productType'>
> & { productType: ProductType }

export type SignatureWorkbookCartItemCreation = CartItemRequest<
  SignatureWorkbookCartItemCreationRequest,
  'SIGNATURE_WORKBOOK'
> & { coverData: CoverDataPayload }
export type CustomWorkbookCartItemCreation = CartItemRequest<
  CustomWorkbookCartItemCreationRequest,
  'CUSTOM_WORKBOOK'
> & { coverData?: CoverDataPayload | null }
export type PrintDocumentCartItemCreation = CartItemRequest<
  PrintDocumentCartItemCreationRequest,
  'PRINT_DOCUMENT'
> & { coverData: CoverDataPayload }
export type CartItemCreation =
  | SignatureWorkbookCartItemCreation
  | CustomWorkbookCartItemCreation
  | PrintDocumentCartItemCreation

export type SignatureWorkbookCartItemUpdate = CartItemRequest<
  SignatureWorkbookCartItemUpdateRequest,
  'SIGNATURE_WORKBOOK'
>
export type CustomWorkbookCartItemUpdate = CartItemRequest<
  CustomWorkbookCartItemUpdateRequest,
  'CUSTOM_WORKBOOK'
>
export type PrintDocumentCartItemUpdate = CartItemRequest<
  PrintDocumentCartItemUpdateRequest,
  'PRINT_DOCUMENT'
>
export type CartItemUpdate =
  | SignatureWorkbookCartItemUpdate
  | CustomWorkbookCartItemUpdate
  | PrintDocumentCartItemUpdate

export type CartListParams = GetCartItemParams

export const getCart = async (params?: CartListParams): Promise<Cart> => {
  const body = await httpClient('pod').get<GetCartItemData>('/cart-item', { params })
  return adaptCart(body)
}

export const createCartItem = async (payload: CartItemCreation): Promise<CartItem['id']> => {
  const body = await httpClient('pod').post<CreateCartItemData>('/cart-item', payload)
  return adaptCartItemCreation(body)
}

export const updateCartItem = async (
  id: CartItem['id'],
  payload: CartItemUpdate,
): Promise<CartItemPrice> => {
  const body = await httpClient('pod').put<UpdateCartItemData>(`/cart-item/${id}`, payload)
  return adaptCartItemPrice(body)
}

export const deleteCartItems = async (ids: CartItem['id'][]): Promise<void> => {
  await httpClient('pod').delete('/cart-item', { params: { ids } })
}

export const getCartItemPdfDownload = async (id: CartItem['id']): Promise<PdfDownload> => {
  const body = await httpClient('pod').get<DownloadCartItemPdfFileData>(
    `/cart-item/v2/download/${id}`,
  )
  return adaptPdfDownload(body)
}
